import React, { useState, useEffect } from 'react';
import { bikes, spareParts, bikeParts } from '../services/api';
import { compressImage } from '../utils/imageUtils';

const MasterBikeParts = () => {
  const [bikeList, setBikeList] = useState([]);
  const [selectedBike, setSelectedBike] = useState(null);
  
  const [partsList, setPartsList] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  
  const [allSpareParts, setAllSpareParts] = useState([]);
  
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [formData, setFormData] = useState({
    spare_part_id: '',
    part_number: '',
    prices: '',
    image: null
  });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [resBikes, resSpareParts] = await Promise.all([
        bikes.getAll(),
        spareParts.getAll() // Note: if there are too many spare parts, this might need an autocomplete, but for now typical implementation is a select.
      ]);
      setBikeList(resBikes.data.data);
      setAllSpareParts(resSpareParts.data.data);
    } catch (err) {
      console.error('Failed to fetch initial data for mapping', err);
    }
  };

  const fetchMappedParts = async (bikeId) => {
    setLoadingParts(true);
    try {
      const res = await bikeParts.getByBike(bikeId);
      // Backend should return JSON with an array inside data
      setPartsList(res.data.data || res.data); 
    } catch (err) {
      console.error('Failed to fetch mapped parts', err);
      setPartsList([]);
    } finally {
      setLoadingParts(false);
    }
  };

  const handleBikeSelect = (bike) => {
    setSelectedBike(bike);
    fetchMappedParts(bike.id);
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        spare_part_id: item.spare_part_id,
        part_number: item.part_number || '',
        prices: item.prices || '',
        image: null
      });
    } else {
      setEditingItem(null);
      setFormData({ spare_part_id: '', part_number: '', prices: '', image: null });
    }
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedBike) return;
    setError('');
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append('spare_part_id', formData.spare_part_id);
      payload.append('part_number', formData.part_number);
      payload.append('prices', formData.prices);
      
      if (formData.image) {
        const compressedFile = await compressImage(formData.image);
        payload.append('image', compressedFile);
      }
      
      if (editingItem) {
        await bikeParts.update(editingItem.bike_part_id, payload);
      } else {
        await bikeParts.create(selectedBike.id, payload);
      }
      setShowModal(false);
      fetchMappedParts(selectedBike.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan pemetaan.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (bike_part_id) => {
    if (!window.confirm('Hapus relasi part untuk motor ini?')) return;
    try {
      await bikeParts.delete(bike_part_id);
      fetchMappedParts(selectedBike.id);
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus data.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Master Mapping & Part Number</h2>
          <p className="text-gray-500 text-sm mt-1">Pilih Motor di sebelah kiri untuk melihat dan mengelola mapping (HET, Part Number) Part-nya.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
        {/* Bike Selector List */}
        <div className="md:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-[600px] overflow-hidden sticky top-6">
          <div className="bg-gray-50 border-b p-4">
            <h3 className="font-bold text-gray-800 text-sm">Daftar Motor</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {bikeList.length === 0 ? (
              <div className="text-center text-gray-400 p-4 text-xs font-medium">Memuat...</div>
            ) : (
              bikeList.map(bike => (
                <button
                  key={bike.id}
                  onClick={() => handleBikeSelect(bike)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-sm mb-1 transition flex justify-between items-center group ${
                    selectedBike?.id === bike.id ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="font-bold truncate">{bike.name}</span>
                  <span className={`text-xs ml-2 flex-shrink-0 ${selectedBike?.id === bike.id ? 'text-blue-200' : 'text-gray-400 group-hover:text-blue-500'}`}>
                    {bike.code}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Mapped Parts Details */}
        <div className="md:col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[600px]">
          {selectedBike ? (
            <>
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-gray-800">Pemetaan Part: {selectedBike.name} <span className="text-blue-600 text-xs">({selectedBike.code})</span></h3>
                  <p className="text-xs text-gray-500 mt-0.5">Pengaturan HET & Nomor Part spesifik untuk motor ini.</p>
                </div>
                <button
                  onClick={() => handleOpenModal()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Mapping Part
                </button>
              </div>

              <div className="flex-1 p-0">
                {loadingParts ? (
                  <div className="text-center py-12 text-gray-500 font-medium">Memuat data pemetaan part...</div>
                ) : partsList.filter(p => p.bike_part_id).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-16 h-16 bg-gray-50 flex items-center justify-center rounded-full text-gray-300 mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 font-medium text-sm">Belum ada part yang dipetakan ke {selectedBike.name}.</p>
                    <button onClick={() => handleOpenModal()} className="mt-4 text-blue-600 hover:underline text-sm font-bold">
                      Tambah Pemetaan Pertama
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Item (Master Part)</th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Part Number</th>
                          <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Harga (HET)</th>
                          <th className="px-6 py-4 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {partsList.filter(p => p.bike_part_id).map((mapped) => (
                          <tr key={mapped.bike_part_id} className="hover:bg-blue-50/50 transition duration-150">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                {mapped.image ? (
                                  <img 
                                    src={mapped.image} 
                                    alt="Part"
                                    className="w-10 h-10 object-cover rounded shadow-sm border"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center text-xs text-gray-400">
                                    No Img
                                  </div>
                                )}
                                <div>
                                  <div className="font-bold text-gray-900 text-sm">
                                    {mapped.spare_part_name || 'Unknown Part'}
                                  </div>
                                  {mapped.is_miscellaneous ? (
                                    <span className="text-[10px] uppercase font-bold text-orange-600 mt-1 block tracking-wider">Insidental</span>
                                  ) : (
                                    <span className="text-[10px] uppercase font-bold text-emerald-600 mt-1 block tracking-wider">Part Utama</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-mono text-xs font-semibold bg-gray-100 px-2 py-1 rounded text-gray-700 border border-gray-200 shadow-sm">
                                {mapped.part_number || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right font-bold text-blue-700 text-sm">
                              {mapped.prices ? formatCurrency(mapped.prices) : '-'}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex justify-center gap-2 text-sm font-medium">
                                <button onClick={() => handleOpenModal(mapped)} className="text-blue-600 hover:text-blue-800 bg-blue-50 px-2.5 py-1.5 rounded-lg transition">Edit</button>
                                <button onClick={() => handleDelete(mapped.bike_part_id)} className="text-red-500 hover:text-red-700 bg-red-50 px-2.5 py-1.5 rounded-lg transition">Hapus</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 py-32 text-center text-gray-400 px-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-6 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <h4 className="text-xl font-bold text-gray-800 mb-2">Pilih Motor</h4>
              <p className="text-sm max-w-sm">Klik pada daftar list motor di sebelah kiri untuk melihat daftar part dan menentukan harga (HET) serta Part Number-nya.</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{editingItem ? 'Edit Mapping Part' : 'Mapping Part Baru'}</h3>
                <p className="text-xs text-blue-600 font-bold mt-1 uppercase tracking-wider">{selectedBike?.name}</p>
              </div>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Pilih Master Spare Part</label>
                <select
                  required
                  disabled={editingItem !== null} // Prevent changing base part when editing to avoid confusion
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white disabled:bg-gray-100"
                  value={formData.spare_part_id}
                  onChange={e => setFormData({ ...formData, spare_part_id: e.target.value })}
                >
                  <option value="">-- Pilih Spare Part --</option>
                  {allSpareParts.map(sp => (
                    <option key={sp.id} value={sp.id}>{sp.name} {sp.is_miscellaneous ? '(Insidental)' : ''}</option>
                  ))}
                </select>
                {editingItem && <p className="text-[10px] text-gray-400 mt-1">Master part tidak bisa diubah saat mode Edit. Buat mapping baru jika salah part.</p>}
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Part Number Asli (Contoh: 15410-K56-N00)</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                  value={formData.part_number}
                  onChange={e => setFormData({ ...formData, part_number: e.target.value.toUpperCase() })}
                  placeholder="Kode Part AHM"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Harga Eceran Tertinggi (HET) - Rp.</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.prices}
                  onChange={e => setFormData({ ...formData, prices: e.target.value })}
                  placeholder="Contoh: 45000"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Gambar Part (Opsional)</label>
                <div className="flex items-center gap-4">
                  {editingItem?.image && !formData.image && (
                    <img 
                      src={editingItem.image} 
                      className="w-12 h-12 rounded object-cover border shadow-sm"
                      alt="Current"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => setFormData({ ...formData, image: e.target.files[0] })}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Format: JPG, PNG. Otomatis dikompres sebelum diunggah.</p>
              </div>

              <div className="flex gap-3 mt-8 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`flex-1 py-2.5 text-white font-bold rounded-xl transition shadow-md ${
                    isSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                  }`}
                >
                  {isSaving ? 'Menyimpan...' : 'Simpan Mapping'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterBikeParts;
